import { useEffect, useState } from 'react';
import { Alert, Card, chooseTheme, Page, PageHeader, Select } from '@evogard/ui';
import { THEMES } from '@evogard/domain';
import type { Theme } from '@evogard/domain';
import { useSessionContext, useSavePreferences } from '../../lib/session.js';

// @valor
const LABEL_THEME: Record<Theme, string> = {
  sistema: 'Do sistema',
  claro: 'Claro',
  escuro: 'Escuro',
};

/**
 * O que a pessoa escolhe para si: tema, base padrão e canais.
 *
 * ## Não há consulta própria aqui
 *
 * A preferência chega no **contexto**, que a SPA já carrega no login
 * (`plataforma-08`). Uma consulta a mais nesta tela seria uma requisição para
 * buscar o que já está em memória — e, pior, o precedente para o tema passar a
 * depender dela e a página piscar no tema errado enquanto espera.
 *
 * O que existe é a mutação: grava, e o servidor devolve o estado que valeu.
 *
 * ## O tema pinta na hora; o resto espera o servidor
 *
 * Trocar cor é a interação em que a resposta imediata é o próprio ponto:
 * esperar meio segundo faz parecer que não funcionou. Então `chooseTheme`
 * pinta antes da resposta, e se a gravação falhar a tela volta ao que o servidor
 * tem — o que ela não faz é ficar mostrando escuro enquanto o servidor guardou
 * claro.
 *
 * Base padrão e canais não têm esse efeito visível, e por isso só mudam depois
 * do 200. Uma tela que diz "salvo" antes de estar salvo é a mesma mentira de
 * interface com um sinal mais discreto.
 */
export function PreferencesScreen() {
  const { data: context } = useSessionContext();
  const [error, setError] = useState<string | null>(null);

  const prefs = context?.preferences;

  /*
   * A mesma mutação que o seletor do menu de conta usa (`lib/session.ts`). Duas
   * cópias divergiriam no primeiro conserto de tratamento de erro, e a do menu
   * é a que ninguém abre para revisar.
   */
  const save = useSavePreferences();

  useEffect(() => {
    if (save.isError) setError((save.error as Error).message);
    if (save.isSuccess) setError(null);
  }, [save.isError, save.isSuccess, save.error]);

  if (!context || !prefs) return null;

  const atProgress = save.isPending;

  return (
    <Page>
      <PageHeader title="Preferências">
        Valem em qualquer aparelho onde você entrar.
      </PageHeader>

      {error && <Alert tone="erro">{error}</Alert>}

      <Card title="Aparência">
        <div className="max-w-xs">
          <Select
            label="Tema"
            value={prefs.theme}
            disabled={atProgress}
            onChange={(e) => {
              const theme = e.target.value as Theme;
              chooseTheme(theme);
              /*
               * A reversão fica no ponto que pintou, e não dentro da mutação
               * compartilhada: só quem pinta antes da resposta tem o que
               * desfazer. Sem ela, a tela ficaria mostrando escuro com o
               * servidor guardando claro — a escolha que não foi salva.
               */
              save.mutate({ theme }, { onError: () => chooseTheme(prefs.theme) });
            }}
            help="“Do sistema” acompanha o ajuste do seu computador."
          >
            {THEMES.map((t) => (
              <option key={t} value={t}>
                {LABEL_THEME[t]}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <Card
        title="Base padrão"
        descricao="Em qual base a sua sessão abre quando você entra."
      >
        <div className="max-w-xs">
          {/*
           * "Abrir nesta base", e não "Base": o seletor de troca de base do
           * chrome já se chama Base, e dois controles com o mesmo rótulo na
           * mesma tela é ambiguidade para quem lê por leitor de tela e para
           * quem escreve teste — foi o teste que reclamou primeiro.
           */}
          <Select
            label="Abrir nesta base"
            value={prefs.defaultTenant === null ? '' : String(prefs.defaultTenant)}
            disabled={atProgress}
            onChange={(e) =>
              save.mutate({
                defaultTenant: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            help="Trocar de base durante a sessão continua valendo até você sair."
          >
            {/*
             * "Nenhuma" é opção de verdade, e não ausência: quem trabalha nas
             * duas bases não quer nenhuma delas fixada, e sem esta linha a
             * única forma de desfazer a escolha seria escolher outra.
             */}
            <option value="">Nenhuma — a primeira da lista</option>
            {context.tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <Card
        title="Avisos por e-mail"
        descricao="Andamento dos tickets em que você está envolvido."
      >
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.channels.email}
            disabled={atProgress}
            onChange={(e) => save.mutate({ channels: { email: e.target.checked } })}
            className="mt-0.5 size-4 shrink-0 rounded border-borda-forte accent-primaria-texto"
          />
          <span>
            Receber avisos por e-mail
            <span className="mt-0.5 block text-xs text-texto-suave">
              Desmarcar vale para todas as bases: a escolha é sua, não da base.
            </span>
          </span>
        </label>

        {/*
         * O que a preferência NÃO desliga.
         *
         * O `NotificationsService` marca convite e redefinição de senha como
         * `essencial`, e uma exceção que existe no código e não na tela é a
         * mesma mentira de interface com o sinal trocado. A tela do associado
         * diz o equivalente, e pelo mesmo motivo.
         */}
        <p className="mt-3 text-xs text-texto-suave">
          Continuam sendo enviados os links para você entrar ou recuperar a senha.
        </p>
      </Card>
    </Page>
  );
}
